<template>
  <!-- การ์ด "ทดสอบถอดเสียง" — ใต้การ์ด Azure | Whisper ของแท็บ Voice (Voxtron)
       เส้นทาง: หน้านี้ → CF settings/voice-stream/test → Fusion.Middleware /api/mw/v1/stt/test → Azure / Whisper
       ⚠️ ใช้ค่าบนหน้าที่ยังไม่บันทึก (getSettings) · ไม่เก็บเสียง · คลิปละ ≤ 15 วิ -->
  <v-card class="qa-card-sm" variant="outlined">
    <v-card-title class="py-3 d-flex align-center flex-wrap" style="gap: 4px">
      <v-icon icon="mdi-microphone-message" size="small" class="mr-2" />
      <span class="text-subtitle-1">{{ $t('qaVsTTitle') }}</span>
      <v-spacer />
      <v-chip size="small" variant="outlined" prepend-icon="mdi-pencil-outline">{{ $t('qaVsTUnsavedChip') }}</v-chip>
    </v-card-title>
    <v-divider />
    <div class="pa-4">
      <div class="text-caption text-medium-emphasis mb-3">{{ $t('qaVsTDesc') }}</div>

      <div class="qa-vst-controls">
        <!-- ① เอาเสียงเข้า: อัปโหลดไฟล์ หรือกดค้างพูด -->
        <div class="qa-vst-input">
          <v-btn variant="outlined" size="large" :disabled="busy || recording" @click="$refs.file.click()">
            <span class="mr-2" aria-hidden="true">📁</span>{{ $t('qaVsTPickFile') }}
          </v-btn>
          <input ref="file" type="file" accept="audio/*,.wav,.mp3,.m4a,.ogg,.webm" class="d-none" @change="onFile" />

          <v-btn
            :color="recording ? 'error' : 'primary'"
            :variant="recording ? 'flat' : 'tonal'"
            size="large"
            class="qa-vst-talk"
            :disabled="busy || !micSupported"
            :aria-pressed="recording"
            @pointerdown.prevent="startRecording"
            @pointerup.prevent="stopRecording"
            @pointerleave="recording && stopRecording()"
            @pointercancel="recording && stopRecording()"
            @keydown.space.prevent="!recording && startRecording()"
            @keyup.space.prevent="stopRecording"
            @contextmenu.prevent
          >
            <span class="mr-2" aria-hidden="true">🎙</span>
            {{ recording ? $t('qaVsTRecording', { sec: elapsedText }) : $t('qaVsTHoldToTalk') }}
          </v-btn>
        </div>

        <!-- ② เลือกตัวถอด แล้วยิงคลิปเดิมซ้ำได้ ไม่ต้องอัด/อัปโหลดใหม่ -->
        <div class="qa-vst-input">
          <span class="text-body-2 font-weight-medium mr-1">{{ $t('qaVsTProvider') }}</span>
          <v-btn-toggle v-model="provider" color="primary" density="compact" variant="outlined" divided mandatory :disabled="busy || recording">
            <v-btn value="azure" prepend-icon="mdi-microsoft-azure">{{ $t('qaVsTProv_azure') }}</v-btn>
            <v-btn value="own" prepend-icon="mdi-server">{{ $t('qaVsTProv_own') }}</v-btn>
            <v-btn value="both" prepend-icon="mdi-compare-horizontal">{{ $t('qaVsTProv_both') }}</v-btn>
          </v-btn-toggle>

          <v-btn color="primary" variant="flat" :disabled="busy || recording || !clip" @click="run()">
            <span class="mr-2" aria-hidden="true">▶</span>{{ $t('qaVsTRunAgain', { prov: providerLabel }) }}
          </v-btn>
        </div>
      </div>
      <div v-if="clip" class="text-caption text-medium-emphasis mt-2">{{ $t('qaVsTReuseHint') }}</div>

      <!-- ระดับเสียงสด + คลื่นย่อ (ขณะอัด) / คลิปที่ส่งล่าสุด -->
      <div class="qa-vst-wave mt-3" :class="{ 'qa-vst-wave--live': recording }">
        <svg :viewBox="`0 0 ${WAVE_BARS * 4} 40`" preserveAspectRatio="none" class="qa-vst-wave__svg" aria-hidden="true">
          <rect
            v-for="(v, i) in bars"
            :key="i"
            :x="i * 4"
            :y="20 - Math.max(0.5, v * 19)"
            width="3"
            :height="Math.max(1, v * 38)"
            rx="1"
          />
        </svg>
        <div class="qa-vst-wave__meta text-caption text-medium-emphasis">
          <span v-if="recording">{{ $t('qaVsTLevel') }} {{ Math.round(level * 100) }}% · {{ elapsedText }} / {{ MAX_SEC }} {{ $t('qaVsUnitSec') }}</span>
          <span v-else-if="clipInfo">{{ clipInfo }}</span>
          <span v-else>{{ micSupported ? $t('qaVsTHint') : $t('qaVsTNoMic') }}</span>
        </div>
      </div>
      <audio v-if="clipUrl && !recording" :src="clipUrl" controls class="qa-vst-audio mt-2" />

      <!-- เวลาของ**การถอด** (คนละเรื่องกับความยาวคลิปด้านบน) -->
      <div v-if="startedAt" class="text-caption text-medium-emphasis mt-2">
        <v-icon icon="mdi-timer-outline" size="x-small" class="mr-1" />
        {{ $t('qaVsTStartedAt', { time: startedAtText }) }}
        <template v-if="result"> · {{ $t('qaVsTRunTook', { sec: sec(result.total_ms) }) }}</template>
        <template v-else-if="busy"> · {{ $t('qaVsTRunning', { sec: runElapsedText }) }}</template>
      </div>

      <v-progress-linear v-if="busy" indeterminate color="primary" class="mt-3" />

      <!-- เครื่องยังไม่ตื่น: คลิปยังอยู่ · ผู้ใช้กดยิงซ้ำเองเมื่อพร้อม (ระบบไม่ยิงเองซ้ำ) -->
      <v-alert v-if="whisperWaking" type="info" variant="tonal" density="compact" class="mt-3">
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <span class="text-body-2">{{ $t('qaVsTWakingHint') }}</span>
          <v-spacer />
          <v-btn size="small" variant="text" :disabled="busy" @click="run()">{{ $t('qaVsTRetryNow') }}</v-btn>
        </div>
      </v-alert>
      <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">
        <span class="text-body-2">{{ error }}</span>
      </v-alert>
      <v-alert v-if="ignored.length" type="info" variant="tonal" density="compact" class="mt-3">
        <span class="text-caption">{{ $t('qaVsTIgnored', { keys: ignored.join(', ') }) }}</span>
      </v-alert>

      <!-- ③ ผลวางคู่กัน: Azure | Whisper -->
      <v-row dense class="mt-2">
        <v-col cols="12" md="6">
          <div class="qa-vst-result" :class="{ 'qa-vst-result--off': !shownAzure }">
            <div class="d-flex align-center mb-2" style="gap: 6px">
              <v-icon icon="mdi-microsoft-azure" size="small" />
              <span class="text-body-2 font-weight-medium">{{ $t('qaVsTAzureResult') }}</span>
              <v-spacer />
              <span v-if="azure" class="text-caption text-medium-emphasis">
                <template v-if="azure.ms">{{ $t('qaVsTTook', { sec: sec(azure.ms) }) }} · </template>{{ $t('qaVsTFromRun', { time: azure.at }) }}
              </span>
            </div>
            <template v-if="azure">
              <div v-if="azure.status === 'ok' && azure.text" class="qa-vst-text">{{ azure.text }}</div>
              <div v-if="azure.lang" class="mt-1">
                <v-chip size="x-small" variant="outlined">{{ azure.lang }}</v-chip>
              </div>
              <v-alert v-if="azure.status !== 'ok' || !azure.text" :type="azure.status === 'ok' ? 'info' : 'warning'" variant="tonal" density="compact" class="mt-2">
                <span class="text-body-2">{{ azureMessage }}</span>
              </v-alert>
            </template>
            <div v-else class="text-caption text-medium-emphasis">{{ $t('qaVsTNotRunYet') }}</div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="qa-vst-result" :class="{ 'qa-vst-result--off': !shownOwn }">
            <div class="d-flex align-center mb-2" style="gap: 6px">
              <v-icon icon="mdi-server" size="small" />
              <span class="text-body-2 font-weight-medium">{{ $t('qaVsTWhisperResult') }}</span>
              <v-spacer />
              <span v-if="own" class="text-caption text-medium-emphasis">
                <template v-if="own.ready_ms != null && own.ready_ms >= 500">{{ $t('qaVsTReadyWait', { sec: sec(own.ready_ms) }) }} · </template>{{ $t('qaVsTTotal', { sec: sec(own.total_ms) }) }} · {{ $t('qaVsTFromRun', { time: own.at }) }}
              </span>
            </div>

            <!-- สถานะเครื่อง Whisper (scale-to-zero) — ค่าล่าสุดที่รู้ -->
            <div class="qa-vst-state mb-2">
              <v-icon :icon="stateView.icon" :color="stateView.color" size="small" class="mr-1" />
              <span class="text-body-2">{{ $t('qaVsTMachine') }}: <b>{{ stateView.label }}</b></span>
              <div v-if="own && own.message" class="text-caption text-medium-emphasis">{{ own.message }}</div>
              <div v-if="own && own.host" class="text-caption text-medium-emphasis">{{ own.host }}</div>
            </div>

            <template v-if="own">
              <div v-if="!own.segments || !own.segments.length" class="text-caption text-medium-emphasis">{{ $t('qaVsTNoSegments') }}</div>
              <div v-for="s in own.segments" :key="s.index" class="qa-vst-seg">
                <div class="d-flex align-center flex-wrap" style="gap: 6px">
                  <span class="text-caption text-medium-emphasis qa-vst-mono">{{ $t('qaVsTSegAudio', { from: sec(s.start_ms), to: sec(s.end_ms) }) }}</span>
                  <v-chip v-if="s.lang" size="x-small" variant="outlined">{{ s.lang }}</v-chip>
                  <v-chip v-if="s.model_short" size="x-small" variant="outlined">{{ s.model_short }}</v-chip>
                  <v-chip size="x-small" :color="segColor(s.status)" variant="tonal">{{ segLabel(s.status) }}</v-chip>
                  <v-spacer />
                  <span v-if="s.sent_at_ms != null" class="text-caption text-medium-emphasis">{{ $t('qaVsTSegStart', { sec: sec(s.sent_at_ms) }) }}</span>
                  <span v-if="s.total_ms != null" class="text-caption text-medium-emphasis">{{ $t('qaVsTSegTook', { sec: sec(s.total_ms) }) }}</span>
                  <span v-if="s.latency_ms != null" class="text-caption text-medium-emphasis">{{ $t('qaVsTSegGpu', { sec: sec(s.latency_ms) }) }}</span>
                </div>
                <div v-if="s.text" class="qa-vst-text mt-1">{{ s.text }}</div>
                <div v-if="s.dropped" class="text-caption text-warning mt-1">{{ $t('qaVsTDroppedBecause', { why: dropLabel(s.dropped) }) }}</div>
                <div v-if="s.message && !s.dropped" class="text-caption text-medium-emphasis mt-1">{{ s.message }}</div>
              </div>
            </template>
            <div v-else class="text-caption text-medium-emphasis">{{ $t('qaVsTNotRunYet') }}</div>
          </div>
        </v-col>
      </v-row>

      <div class="text-caption text-medium-emphasis mt-3">
        <v-icon icon="mdi-cash" size="x-small" class="mr-1" />{{ $t('qaVsTCost') }}
      </div>
    </div>
  </v-card>
</template>

<script>
/**
 * การ์ด "ทดสอบถอดเสียง" (แท็บ Voice (Voxtron)) — กดค้างพูด / เลือกไฟล์ → Middleware → Azure | Whisper → ข้อความ
 *
 * เสียง: AudioWorklet (inline · blob URL) → mono → 16 kHz → PCM16 (แบบเดียวกับ vad_sandbox/wwwroot/worklet.js)
 *        ไฟล์: decodeAudioData แล้ว OfflineAudioContext 16 kHz mono · เกิน 15 วิ ตัดเหลือ 15 วิแรก
 * ⚠️ ส่งค่าบนหน้าที่ยังไม่บันทึก (getSettings = toPayload ของหน้าแม่) · ไม่มีช่องคีย์ (คีย์อยู่ที่ middleware)
 * ⚠️ เสียงอยู่ในหน่วยความจำของเบราว์เซอร์เท่านั้น ไม่ได้เก็บที่ไหน
 */
import qaConsole from '@/services/qaConsole';

const RATE = 16000;
const MAX_SEC = 15;
const MIN_SEC = 0.3;
const WAVE_BARS = 90;

/** worklet แบบ inline — ไม่ต้องตั้งค่า asset ของ Vite (addModule รับ blob URL ได้) */
const WORKLET_SRC = `
class Down16k extends AudioWorkletProcessor {
  constructor() { super(); this.ratio = sampleRate / ${RATE}; this.pos = 0; this.acc = 0; this.cnt = 0; this.out = new Float32Array(512); this.n = 0; }
  emit(v) { this.out[this.n++] = v; if (this.n === 512) { this.port.postMessage(this.out.slice(0)); this.n = 0; } }
  process(inputs) {
    const input = inputs[0];
    if (!input || input.length === 0) return true;
    const a = input[0]; const b = input.length > 1 ? input[1] : null;
    for (let i = 0; i < a.length; i++) {
      const x = b ? (a[i] + b[i]) * 0.5 : a[i];
      if (this.ratio === 1) { this.emit(x); continue; }
      this.acc += x; this.cnt++; this.pos += 1;
      if (this.pos >= this.ratio) { this.pos -= this.ratio; this.emit(this.acc / this.cnt); this.acc = 0; this.cnt = 0; }
    }
    return true;
  }
}
registerProcessor('down16k', Down16k);
`;

function floatToPcm16(samples) {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function bytesToBase64(bytes) {
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** PCM16 mono 16 kHz → WAV (ไว้ฟังคลิปที่ส่งไป) */
function wavUrl(pcm16) {
  const data = new Uint8Array(pcm16.buffer, pcm16.byteOffset, pcm16.byteLength);
  const buf = new ArrayBuffer(44 + data.length);
  const v = new DataView(buf);
  const str = (o, s) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF'); v.setUint32(4, 36 + data.length, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, RATE, true); v.setUint32(28, RATE * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, data.length, true);
  new Uint8Array(buf, 44).set(data);
  return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
}

function rms(frame) {
  let sum = 0;
  for (let i = 0; i < frame.length; i += 1) sum += frame[i] * frame[i];
  return Math.sqrt(sum / Math.max(1, frame.length));
}

/** RMS → 0..1 แบบ dB (−60 dBFS = 0 · 0 dBFS = 1) ให้เสียงพูดปกติขึ้นกลาง ๆ แถบ */
function toLevel(r) {
  const db = 20 * Math.log10(r + 1e-9);
  return Math.max(0, Math.min(1, (db + 60) / 60));
}

export default {
  name: 'QaVoiceSttTestCard',
  props: {
    /** ตัวที่เลือกไว้ด้านบน (azure | own) — ค่าตั้งต้นของปุ่มเลือก */
    defaultProvider: { type: String, default: 'azure' },
    /** คืนค่าตั้ง voice-stream บนหน้า (ยังไม่บันทึก) ในรูปที่ส่ง server ได้ */
    getSettings: { type: Function, required: true },
  },
  data() {
    return {
      provider: this.defaultProvider === 'own' ? 'own' : 'azure',
      recording: false,
      busy: false,
      level: 0,
      bars: new Array(WAVE_BARS).fill(0),
      elapsed: 0,
      error: '',
      result: null,          // รอบล่าสุด (ไว้ดูเวลารวม)
      res: { azure: null, own: null },   // ผลล่าสุด**ของแต่ละตัวถอด** ต่อคลิปนี้ — ยิง Whisper แล้วผล Azure ต้องไม่หาย
      ignored: [],
      lastState: null,
      clipUrl: '',
      clipInfo: '',
      clip: null,            // คลิปล่าสุด (base64 PCM16) — ยิงซ้ำกับตัวถอดอื่นได้ ไม่ต้องอัด/อัปโหลดใหม่
      startedAt: null,       // เวลานาฬิกาที่เริ่มถอดรอบล่าสุด
      runElapsed: 0,
      micSupported: typeof window !== 'undefined' && !!(navigator.mediaDevices && window.AudioWorkletNode),
    };
  },
  computed: {
    MAX_SEC: () => MAX_SEC,
    WAVE_BARS: () => WAVE_BARS,
    elapsedText() {
      return this.elapsed.toFixed(1);
    },
    runElapsedText() {
      return this.runElapsed.toFixed(1);
    },
    startedAtText() {
      return this.startedAt ? this.startedAt.toLocaleTimeString() : '';
    },
    providerLabel() {
      return this.$t(`qaVsTProv_${this.provider}`);
    },
    /** รอบล่าสุดเครื่อง Whisper ยังไม่ตื่น = ท่อนไม่ได้ถูกส่ง · คลิปยังอยู่ กดยิงซ้ำได้เมื่อพร้อม */
    whisperWaking() {
      return this.lastState === 'asleep' || this.lastState === 'waking';
    },
    /** จางลงเมื่อผลในคอลัมน์นั้นไม่ได้มาจากรอบล่าสุด (ยังอ่านได้ ไว้เทียบกัน) */
    shownAzure() {
      return !!(this.result && this.result.azure);
    },
    shownOwn() {
      return !!(this.result && this.result.own);
    },
    azure() {
      return this.res.azure;
    },
    own() {
      return this.res.own;
    },
    azureMessage() {
      const a = this.azure;
      if (!a) return '';
      if (a.status === 'not_configured') return this.$t('qaVsTAzureNotConfigured');
      if (a.status === 'ok' && !a.text) return this.$t('qaVsTAzureNoSpeech');
      if (a.status === 'timeout') return this.$t('qaVsTAzureTimeout');
      return a.message || this.$t('qaVsTAzureError');
    },
    stateView() {
      const s = this.lastState;
      const map = {
        ready: { icon: 'mdi-check-circle', color: 'success', label: this.$t('qaVsTState_ready') },
        asleep: { icon: 'mdi-sleep', color: 'grey', label: this.$t('qaVsTState_asleep') },
        waking: { icon: 'mdi-timer-sand', color: 'warning', label: this.$t('qaVsTState_waking') },
        stopped: { icon: 'mdi-power-plug-off', color: 'error', label: this.$t('qaVsTState_stopped') },
        unreachable: { icon: 'mdi-lan-disconnect', color: 'error', label: this.$t('qaVsTState_unreachable') },
        not_configured: { icon: 'mdi-link-off', color: 'warning', label: this.$t('qaVsTState_not_configured') },
        no_key: { icon: 'mdi-key-remove', color: 'error', label: this.$t('qaVsTState_no_key') },
        error: { icon: 'mdi-alert-circle', color: 'error', label: this.$t('qaVsTState_error') },
      };
      return map[s] || { icon: 'mdi-help-circle-outline', color: 'grey', label: this.$t('qaVsTState_unknown') };
    },
  },
  watch: {
    defaultProvider(v) {
      if (!this.result && !this.busy) this.provider = v === 'own' ? 'own' : 'azure';
    },
  },
  beforeUnmount() {
    this.teardown();
    if (this.runTimer) clearInterval(this.runTimer);
    if (this.clipUrl) URL.revokeObjectURL(this.clipUrl);
  },
  methods: {
    sec(ms) {
      return ms == null ? '-' : (Number(ms) / 1000).toFixed(1);
    },
    segLabel(status) {
      const key = `qaVsTSeg_${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    segColor(status) {
      return { ok: 'success', dropped: 'warning', not_sent: 'grey', waking: 'warning' }[status] || 'error';
    },
    dropLabel(code) {
      const key = `qaVsTDrop_${code}`;
      return this.$te(key) ? this.$t(key) : code;
    },

    // ── ไมค์ ────────────────────────────────────────────────────────────────
    async startRecording() {
      if (this.recording || this.busy) return;
      this.error = '';
      this.chunks = [];
      this.samples = 0;
      this.bars = new Array(WAVE_BARS).fill(0);
      this.elapsed = 0;
      this.recording = true;
      this.releaseEarly = false;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
        try {
          this.ctx = new AudioContext({ sampleRate: RATE });
        } catch {
          this.ctx = new AudioContext();   // เบราว์เซอร์ที่ไม่ยอม 16 kHz — worklet ลดเองด้วย ratio
        }
        const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: 'application/javascript' }));
        try {
          await this.ctx.audioWorklet.addModule(url);
        } finally {
          URL.revokeObjectURL(url);
        }
        this.source = this.ctx.createMediaStreamSource(this.stream);
        this.node = new AudioWorkletNode(this.ctx, 'down16k');
        this.node.port.onmessage = (e) => this.onFrame(e.data);
        this.source.connect(this.node);
        if (this.releaseEarly) this.stopRecording();   // ปล่อยปุ่มก่อนไมค์พร้อม
      } catch (err) {
        this.teardown();
        this.recording = false;
        this.error = this.$t('qaVsTMicFailed', { msg: (err && err.message) || err });
      }
    },
    onFrame(frame) {
      if (!this.recording) return;
      const room = MAX_SEC * RATE - this.samples;
      const take = room < frame.length ? frame.subarray(0, room) : frame;
      this.chunks.push(take);
      this.samples += take.length;
      this.elapsed = this.samples / RATE;
      this.level = toLevel(rms(take));
      this.bars = [...this.bars.slice(1), this.level];
      if (this.samples >= MAX_SEC * RATE) this.stopRecording();
    },
    stopRecording() {
      if (!this.recording) return;
      if (!this.node) {
        this.releaseEarly = true;
        return;
      }
      this.recording = false;
      this.level = 0;
      const chunks = this.chunks || [];
      this.teardown();
      const all = new Float32Array(this.samples);
      let o = 0;
      chunks.forEach((c) => {
        all.set(c, o);
        o += c.length;
      });
      if (all.length < MIN_SEC * RATE) {
        this.error = this.$t('qaVsTTooShort');
        return;
      }
      this.setClip(all, this.$t('qaVsTClipMic', { sec: (all.length / RATE).toFixed(1) }));
    },
    teardown() {
      try { if (this.node) this.node.port.onmessage = null; } catch { /* ปิดแล้ว */ }
      try { if (this.source) this.source.disconnect(); } catch { /* ปิดแล้ว */ }
      try { if (this.stream) this.stream.getTracks().forEach((t) => t.stop()); } catch { /* ปิดแล้ว */ }
      try { if (this.ctx) this.ctx.close(); } catch { /* ปิดแล้ว */ }
      this.node = null;
      this.source = null;
      this.stream = null;
      this.ctx = null;
    },

    // ── ไฟล์ ────────────────────────────────────────────────────────────────
    async onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      this.error = '';
      if (file.size > 50 * 1024 * 1024) {
        this.error = this.$t('qaVsTFileTooBig');
        return;
      }
      try {
        const bytes = await file.arrayBuffer();
        const decoder = new AudioContext();
        let decoded;
        try {
          decoded = await decoder.decodeAudioData(bytes);
        } finally {
          decoder.close();
        }
        const seconds = Math.min(decoded.duration, MAX_SEC);
        const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(seconds * RATE)), RATE);
        const src = offline.createBufferSource();
        src.buffer = decoded;
        src.connect(offline.destination);   // หลายช่อง → 1 ช่อง (down-mix) + resample เป็น 16 kHz
        src.start(0);
        const rendered = await offline.startRendering();
        const mono = rendered.getChannelData(0);
        if (mono.length < MIN_SEC * RATE) {
          this.error = this.$t('qaVsTTooShort');
          return;
        }
        this.bars = this.overview(mono);
        const trimmed = decoded.duration > MAX_SEC;
        this.setClip(mono, this.$t(trimmed ? 'qaVsTClipFileTrimmed' : 'qaVsTClipFile', {
          name: file.name, sec: seconds.toFixed(1), full: decoded.duration.toFixed(1),
        }));
      } catch (err) {
        this.error = this.$t('qaVsTDecodeFailed', { msg: (err && err.message) || err });
      }
    },
    /** คลื่นย่อของทั้งคลิป (ใช้แถบเดียวกับตอนอัด) */
    overview(samples) {
      const per = Math.max(1, Math.floor(samples.length / WAVE_BARS));
      const out = [];
      for (let i = 0; i < WAVE_BARS; i += 1) out.push(toLevel(rms(samples.subarray(i * per, (i + 1) * per))));
      return out;
    },

    // ── ส่ง ────────────────────────────────────────────────────────────────
    /**
     * เก็บคลิปไว้ในหน่วยความจำเฉย ๆ — **ไม่ยิงเอง**
     * ผู้ใช้ฟังก่อน เลือกตัวถอด แล้วกด ▶ ยืนยันเองทุกครั้ง (กันยิง Azure/GPU โดยไม่ตั้งใจ)
     * ⚠️ เสียงอยู่ในแท็บนี้เท่านั้น ไม่ได้เก็บที่ไหน
     */
    setClip(floatSamples, info) {
      const pcm = floatToPcm16(floatSamples);
      if (this.clipUrl) URL.revokeObjectURL(this.clipUrl);
      this.clipUrl = wavUrl(pcm);
      this.clipInfo = info;
      this.clip = { base64: bytesToBase64(new Uint8Array(pcm.buffer)) };
      this.result = null;
      this.res = { azure: null, own: null };   // คลิปใหม่ = ผลเก่าใช้ไม่ได้แล้ว
      this.startedAt = null;
      this.error = '';
      this.ignored = [];
    },

    /**
     * ยิงคลิปที่เก็บไว้ไปทดสอบ — เปลี่ยนตัวถอดแล้วกดซ้ำได้โดยไม่ต้องอัด/อัปโหลดใหม่
     * manual = ผู้ใช้กดเอง (คืนโควตาลองใหม่อัตโนมัติ) · false = ตัวนับถอยหลังยิงเอง
     */
    /** ยิงคลิปที่เก็บไว้ไปทดสอบด้วยตัวถอดที่เลือกอยู่ — ยิงเมื่อผู้ใช้กดเท่านั้น ไม่มีการยิงเองซ้ำ */
    async run() {
      if (!this.clip || this.busy) return;
      this.busy = true;
      this.error = '';
      this.ignored = [];
      this.startedAt = new Date();
      this.runElapsed = 0;
      this.runTimer = setInterval(() => { this.runElapsed += 0.1; }, 100);
      try {
        const res = await qaConsole.testVoiceStream({
          audioBase64: this.clip.base64,
          format: 'pcm16',
          provider: this.provider,
          settings: this.getSettings(),
        });
        if (!res || res.outcome !== 'ok') {
          this.error = (res && res.message) || this.$t('qaVsTFailed');
          return;
        }
        const r = res.result || {};
        this.result = r;
        this.ignored = Array.isArray(res.settingsIgnored) ? res.settingsIgnored : [];
        const at = this.startedAtText;
        if (r.azure) this.res.azure = { ...r.azure, at };
        if (r.own) {
          this.res.own = { ...r.own, at };
          this.lastState = r.own.state;
        }
      } catch (err) {
        this.error = this.$t('qaVsTFailedMsg', { msg: err.message });
      } finally {
        clearInterval(this.runTimer);
        this.runTimer = null;
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.qa-vst-controls {
  display: flex;
  flex-direction: column;   /* บรรทัดบน = เอาเสียงเข้า · บรรทัดล่าง = เลือกตัวถอดแล้วยิง */
  gap: 12px;
}
.qa-vst-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.qa-vst-talk {
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
}
.qa-vst-wave {
  border-radius: 6px;
  padding: 8px 12px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.qa-vst-wave__svg {
  display: block;
  width: 100%;
  height: 40px;
  fill: rgba(var(--v-theme-primary), 0.55);
}
.qa-vst-wave--live .qa-vst-wave__svg {
  fill: rgb(var(--v-theme-error));
}
.qa-vst-audio {
  width: 100%;
  max-width: 420px;
  height: 32px;
}
.qa-vst-result {
  height: 100%;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.qa-vst-result--off {
  opacity: 0.55;
}
.qa-vst-seg {
  padding: 8px 0;
  border-top: 1px solid #f1f3f5;
}
.qa-vst-text {
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.qa-vst-mono {
  font-family: monospace;
}
</style>
